var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { intersectionObserver } from '../_utils/dom';
import { createPrefixCls, transformPx } from '../_utils/string';
const ww = window.innerWidth;
const wh = window.innerHeight;
const prefixCls = createPrefixCls('image');
/**
 * 图片加载状态
 */
const LOAD_STATUS = {
    /** 未加载 */
    UNLOAD: 'unload',
    /** 正在加载 */
    LOADING: 'loading',
    /** 加载完成 */
    LOADED: 'loaded'
};
function useImageLoad(onLoad) {
    const imageNode = useMemo(() => new window.Image(), []);
    useEffect(() => {
        imageNode.onload = (e) => {
            onLoad && onLoad(e);
        };
        return () => {
            imageNode.onload = null;
        };
    }, [imageNode, onLoad]);
    return imageNode;
}
function useInViewport(threshold, onInViewport) {
    const node = useRef();
    const doLoad = useCallback(() => {
        if (node.current) {
            const { top, left, width, height } = node.current.getBoundingClientRect();
            if (top > -(height - Math.min(threshold, height)) &&
                top < wh - threshold &&
                left > -(width - Math.min(threshold, width)) &&
                left < ww - threshold) {
                onInViewport && onInViewport();
            }
        }
    }, [node, onInViewport, threshold]);
    return [node, doLoad];
}
export const Image = (_a) => {
    var { src, fit, circle, radius, className, style, width, height, lazy, animation = 'fade', fallback, threshold = 10, onLoad } = _a, restProps = __rest(_a, ["src", "fit", "circle", "radius", "className", "style", "width", "height", "lazy", "animation", "fallback", "threshold", "onLoad"]);
    const [loadStatus, setLoadStatus] = useState(LOAD_STATUS.UNLOAD);
    const imageNode = useImageLoad(() => {
        setLoadStatus(LOAD_STATUS.LOADED);
    });
    const onInViewport = useCallback(() => {
        imageNode.src = src;
        setLoadStatus(LOAD_STATUS.LOADING);
    }, [imageNode, src]);
    const [slotNode, doLoad] = useInViewport(threshold, onInViewport);
    useEffect(() => {
        if (!lazy) {
            return;
        }
        const throttleDoLoad = throttle(doLoad, 200);
        window.addEventListener('scroll', throttleDoLoad);
        window.addEventListener('touchend', doLoad);
        if (window.IntersectionObserver) {
            slotNode.current && intersectionObserver(slotNode.current, doLoad);
        }
        else {
            doLoad();
        }
        return () => {
            window.removeEventListener('scroll', throttleDoLoad);
            window.addEventListener('touchend', doLoad);
        };
    }, [doLoad, lazy, slotNode]);
    const handleLoad = useCallback((e) => {
        onLoad && onLoad(e);
    }, [onLoad]);
    const imageCls = useMemo(() => {
        return classnames([
            prefixCls(),
            {
                [prefixCls('lazy-animation')]: lazy,
                [prefixCls(`lazy-animation-${animation}`)]: lazy,
                [prefixCls(`fit-${fit}`)]: fit,
                [prefixCls('circle')]: circle
            },
            className
        ]);
    }, [animation, circle, className, fit, lazy]);
    const imageSty = useMemo(() => {
        return Object.assign(Object.assign({}, style), { borderRadius: radius });
    }, [radius, style]);
    const slotCls = useMemo(() => {
        return classnames(prefixCls('slot'), imageCls.replace(prefixCls('lazy-animation'), '').replace(prefixCls(`lazy-animation-${animation}`), ''));
    }, [animation, imageCls]);
    const slotSty = useMemo(() => {
        return Object.assign(Object.assign({}, imageSty), { width: transformPx(lazy && imageSty.width ? imageSty.width : width), height: transformPx(lazy && imageSty.height ? imageSty.height : height) });
    }, [height, imageSty, lazy, width]);
    if (lazy && loadStatus !== LOAD_STATUS.LOADED) {
        if (fallback) {
            return (React.createElement("div", { ref: slotNode, className: prefixCls('slot-container') }, fallback));
        }
        else {
            return React.createElement("div", Object.assign({ ref: slotNode, className: slotCls, style: slotSty }, restProps));
        }
    }
    else {
        return (React.createElement("img", Object.assign({ src: src, className: imageCls, style: imageSty, width: width, height: height, onLoad: handleLoad }, restProps)));
    }
};
export default Image;
